/**
 * O detalhe por documento da mesa, por expansão (E162.F2.T02, QA-18, ADR-0111 §2.6).
 *
 * O que era a mesa inteira — o perfil do exame, a tabela dos documentos, o quadro por eixos com as
 * sete abas e a decisão por peça — fica atrás de "Ver detalhe por documento", e a Conferência das
 * etapas da pré-análise passa a ser alcançável daqui. Nada é montado (nem lido do servidor) antes
 * de o perito abrir. A CCB se decide no alto da mesa; o apoio aparece conferido com a verificação,
 * e só o apoio que o perito concluiu à mão (antes da E162) mantém a decisão aqui, para reabrir.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { ConferenciaDasEtapas } from '@/components/ConferenciaDasEtapas/ConferenciaDasEtapas';
import { ColunaDeDecisao } from '@/components/ExameEletronico/ColunaDeDecisao';
import type { DecisaoAcao } from '@/components/ExameEletronico/DecisaoDoPerito';
import { PainelDoCaso } from '@/components/ExameEletronico/PainelDoCaso';
import { PerfilDoExame } from '@/components/Formalizacao/PerfilDoExame';
import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ConclusaoEletronicaInput,
  ExameEletronicoCcbData,
  ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';
import { exigeConclusao, pecaConcluida } from '@/lib/exame_eletronico_ccb';

import { QuadroDaPeca } from './QuadroDaPeca';

const t = ptBR.mesaEletronica;

type PorCcb<T> = Readonly<Record<string, T>>;

export interface DetalhePorDocumentoProps {
  client: AxiosInstance;
  caseId: string;
  protocolo: ProtocoloAssinaturaCasoData | null;
  pecas: readonly ExameEletronicoCcbData[];
  acaoPorCcb: PorCcb<DecisaoAcao>;
  erroPorCcb: PorCcb<string>;
  sucessoPorCcb: PorCcb<string>;
  onConcluir: (docId: string, conclusao: ConclusaoEletronicaInput) => void;
  onReabrir: (docId: string, motivo: string) => void;
  onAbrirPreliminar: () => void;
  onMudou: () => void;
}

function AoLadoDoQuadro(props: DetalhePorDocumentoProps & { peca: ExameEletronicoCcbData }) {
  const { peca, pecas } = props;
  const docId = peca.source_document_id;
  const legado = !exigeConclusao(peca) && peca.exame?.status === 'concluido';
  if (legado) {
    return (
      <ColunaDeDecisao
        pecas={[peca]}
        peca={peca}
        indice={pecas.indexOf(peca)}
        acaoEmCurso={props.acaoPorCcb[docId] ?? null}
        erro={props.erroPorCcb[docId]}
        sucesso={props.sucessoPorCcb[docId]}
        onConcluir={(conclusao) => props.onConcluir(docId, conclusao)}
        onReabrir={(motivo) => props.onReabrir(docId, motivo)}
        onSelecionar={() => undefined}
      />
    );
  }
  let texto: string = t.decisaoNoAlto;
  if (!exigeConclusao(peca)) texto = pecaConcluida(peca) ? t.apoioConferido : t.apoioAguarda;
  return (
    <Blueprint as="aside" className="self-start p-sc-6" data-testid="mesa-detalhe-situacao">
      <p className="text-body-sm text-fg">{texto}</p>
    </Blueprint>
  );
}

function Aberto(props: DetalhePorDocumentoProps): JSX.Element {
  const { pecas } = props;
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const indice = Math.max(
    pecas.findIndex((p) => p.source_document_id === selecionada),
    0,
  );
  const aberta = pecas[indice];
  return (
    <div className="space-y-sc-6">
      {/* E151.F2 (GUS-174, ADR-0103 §4.2): o perfil e as regras do exame, lidos do servidor. */}
      <PerfilDoExame client={props.client} caseId={props.caseId} releitura={pecas} />
      {aberta !== undefined ? (
        <>
          <PainelDoCaso
            protocolo={props.protocolo}
            pecas={pecas}
            selecionada={aberta.source_document_id}
            onSelecionar={setSelecionada}
          />
          <div className="grid gap-sc-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
            <QuadroDaPeca
              key={aberta.source_document_id}
              client={props.client}
              caseId={props.caseId}
              peca={aberta}
              indice={indice}
              onAbrirPreliminar={props.onAbrirPreliminar}
            />
            <AoLadoDoQuadro {...props} peca={aberta} />
          </div>
        </>
      ) : null}
      <ConferenciaDasEtapas
        client={props.client}
        caseId={props.caseId}
        releitura={pecas}
        onMudou={props.onMudou}
      />
    </div>
  );
}

export function DetalhePorDocumento(props: DetalhePorDocumentoProps): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <details
      className="mt-sc-8 border-t border-border pt-sc-4"
      data-testid="mesa-detalhe-por-documento"
      onToggle={(evento) => setAberto(evento.currentTarget.open)}
    >
      <summary className="cursor-pointer text-body-md font-medium text-accent-700">
        {t.detalheTitulo}
      </summary>
      {aberto ? (
        <div className="mt-sc-4">
          <Aberto {...props} />
        </div>
      ) : null}
    </details>
  );
}
